/**
 * The Official Profile's remaining panels — `04-officials-profiles.md` §B3–§B11, drawn to
 * the agreed mockup `docs/design/official-profile-detail-v1.html`.
 *
 * **Why they exist now, and what they promise.** Until run 21C these panels were named in a
 * single `Specified, Not Yet Built` foot and drawn nowhere, so the page bore no resemblance
 * to the layout the Technical Delegate signed off. The mockup is the authority on visuals, so
 * every panel it draws is now on the page, in its position, with its heading, its note, its
 * table headings and its foot.
 *
 * **What they never do is invent data.** Where this build has no store behind a panel, the
 * panel says so in one plain sentence and draws nothing else — no sample rows, no zeros, no
 * placeholder prose. One such absence would be actively misleading if drawn as a figure:
 *
 *  · **Cards Issued** would read `0 / 0 / 0` on an umpire who has in fact issued cards. The
 *    tiles are drawn with the standing card icons and the counts withheld, so the panel keeps
 *    its shape without asserting a disciplinary record.
 *
 * **SUPERSEDED, run 51 (2026-09-03) — `TD Feedback` and `Report Readiness` now have a store.**
 * This header said until today that *"`TD Feedback` still has no store and still says so"*, and
 * that §B10's ban on `0%` left the readiness ring undrawable. Both statements were true of the
 * build that wrote them and are false now. Alfred's ruling (2026-09-03): *"Keep the TD Feedback
 * panel and show Quick Notes data so TD can look at previous feedbacks on the same page where
 * the TD will draft his report."* The store is `td_note` (migration 037) — the same notes
 * `Quick Notes` lists — so:
 *
 *  · `TD Feedback` reads those notes, grouped by the grading category each was filed against,
 *    and is READ-ONLY: a correction is made in `Quick Notes`, on the note's own id, and never
 *    twice on one page.
 *  · `Report Readiness` draws a real percentage over `covered / total` grading categories. §B10
 *    forbade `0%` because it *"reads as a real finding"* when the figure is unknown — and with a
 *    store behind it, `0%` **is** a real finding: nothing has been recorded about this official
 *    yet, which is precisely what a TD about to draft needs to see.
 *
 * Both read the ONE fetch {@link useTdNotes} makes for the page, so the notes list, the grouped
 * feedback and the ring can never disagree, and a correction saved in `Quick Notes` moves the
 * ring without a second round trip.
 *
 * **Ruling R-4′ is untouched, and run 30 did not touch it either.** `Quick Notes` now reads
 * the TD's OWN stream (`td_note`, migration 037) — his notes, on his screen, about technical
 * officials. That is a different table, a different route file and a different guard from the
 * Umpires Managers' notes: no count, timestamp, content or existence signal about a UM's
 * unreleased notes can reach this page, because no panel here reads a UM note route at all.
 * Run 51 widens what is drawn from that stream, not whose stream it is.
 *
 * **§B8's conditional render is kept.** `Cards Issued` is an umpire-only panel and is removed
 * from the DOM entirely — not shown empty — for a Technical Officer, Judge, ATD or Umpires
 * Manager. The page decides; this module only draws it.
 */

import { Fragment, useEffect, useState } from "react";

import {
  TD_NOTE_GENERAL_LABEL,
  readinessFraction,
  tdNoteCategoriesForRole,
  tdNoteCategoryLabel,
  tidyCriterionDisplayName,
  type OfficialRole,
} from "@hockeytd/shared";

import { fetchTdNotes, putOfficialTdExpectation, putTdNote } from "../../api/client.js";
import type {
  OfficialAppointedDutiesBody,
  OfficialDetailView,
  TdNoteView,
} from "../../api/types.js";
import { CountryFlag } from "../../app/CountryFlag.js";
import { EmptyState, FailBanner, Skeleton } from "../../app/states.js";
import { tournamentDay, tournamentDayAndClock } from "../../app/tournamentTime.js";
import { useAsync } from "../../app/useAsync.js";
import { CAP_UNIT_SHORT, capTooltip } from "../reporting/capUnit.js";
import { dayLabel } from "../schedule/matchDays.js";

/**
 * §B6's `Gender` column, in words. The column is the gender of the COMPETITIONS the caps were
 * earned in, not the official's own, so a bare `m` beside a woman's name would be read as a
 * claim about her rather than about the tournaments she officiated.
 */
const GENDER_WORD = { m: "Men", f: "Women" } as const;

/**
 * A cap date as §B6 prints it — the stored `YYYY-MM-DD`, or the honest em dash where TMS
 * carries none. Nothing is reformatted: the column is four characters wide in the mockup and
 * the year is the part a TD reads off it.
 */
function capDate(value: string | null): React.ReactNode {
  return value === null ? <span className="cell-none">—</span> : value;
}

/* ────────────────────────────── §B3 · Appointed Duties ──────────────────── */

/**
 * §B3's table, drawn from the store at last — every duty this person holds across the whole
 * tournament, technical and umpire alike.
 *
 * **What this replaced.** Since run 21C the panel was a heading, four column headings and one
 * plain sentence saying there was nothing to draw: *"technical appointments are held one
 * matchday at a time … and umpire appointments are not stored in this build at all"*. Both
 * halves of that are now false, so the panel is drawn rather than excused — and the sentence
 * goes rather than being softened, because a panel that says it has no store while showing one
 * is worse than either.
 *
 * **The columns are §B3's, with `Status` added and `Fixture` split.** §B3 asks for `Match #` ·
 * `Fixture` · `Date` · `Role`; the Technical Delegate asked for the date first (it is what the
 * table is read in order of), for the competition chip beside the number (`M02` alone names two
 * different matches, one Men's and one Women's) and for a column saying whether the duty has
 * been done. Nothing is dropped: the chip, the number, the teams and the slot box are all still
 * here, in §B3's own classes.
 *
 * **Two statuses and no more.** `Completed` when TMS has finished with the MATCH, `Appointed`
 * otherwise — the server's test, so the three surfaces that ask it cannot disagree. It is
 * deliberately not the appointment's own status and not its lock: this column answers *has this
 * been done*, which is a fact about the match rather than about how far the paperwork has got.
 *
 * **It never invents a row.** No duties is a plain sentence, not a zero and not an empty grid.
 */
export function AppointedDutiesPanel({
  duties,
}: {
  /** The page's own read. `null` while it is loading, or when it refused. */
  duties: OfficialAppointedDutiesBody | null;
}): React.JSX.Element {
  const rows = duties?.duties ?? [];

  return (
    <section className="panel panel--d1" aria-labelledby="apptTitle" data-howto="appointment-table">
      <div className="panel__head">
        <h3 className="panel__title" id="apptTitle">
          Appointed Duties
        </h3>
        <span className="spacer" />
        <span className="panel__note">This Tournament</span>
      </div>

      {duties === null ? null : (
        <div className="totalline" data-testid="duties-tally">
          <span className="k">Appointed Duties</span>
          <span className="v num">
            {duties.count} {duties.count === 1 ? "duty" : "duties"} · {duties.completed} completed
          </span>
        </div>
      )}

      {rows.length === 0 ? (
        duties === null ? null : (
          <EmptyState
            title="No Duties Appointed Yet"
            detail="Nothing in this tournament names this official — no TO, SJ or TJ slot on the Technical Delegate's working appointments, and no U1, U2, RU or VU slot in TMS's own record. A duty appointed on either side appears here without this page being reloaded twice."
          />
        )
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table
            className="mtable apptable"
            aria-label="Appointed duties for this official at this tournament"
          >
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Match</th>
                <th scope="col">Teams</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((duty) => (
                <tr
                  key={`${String(duty.tmsMatchDbId)}-${duty.slot}`}
                  data-comp={duty.competitionGender}
                  data-testid={`duty-${String(duty.tmsMatchDbId)}-${duty.slot}`}
                >
                  {/* `.cell-date` and `.cell-match` are the Fixture List's own cells, reused
                      rather than re-styled: `.mno`, `.teams` and `.tm` are scoped to them in
                      the stylesheet, so the date, the number and the teams read here exactly as
                      they do on the appointing page. */}
                  <td className="cell-date">
                    {dayLabel(duty.date)}
                    <span className="tm num">{duty.time}</span>
                  </td>
                  <td className="cell-match">
                    {/* The chip is not decoration: `M02` is a Men's match and a Women's one. */}
                    <span
                      className={`comp-tag${duty.competitionGender === "w" ? " comp-tag--w" : ""}`}
                    >
                      {duty.competitionGender === "w" ? "Women" : "Men"}
                    </span>
                    <span className="mno num">{duty.displayMatchNo}</span>
                  </td>
                  <td className="cell-match">
                    <span className="teams">
                      {side(duty.homeTeamCode, duty.homePlaceholder)} <i>v</i>{" "}
                      {side(duty.awayTeamCode, duty.awayPlaceholder)}
                    </span>
                  </td>
                  <td>
                    {/* §B3: `.rolebox--vu` for the two off-field umpire slots. */}
                    <span
                      className={
                        duty.slot === "VU" || duty.slot === "RU" ? "rolebox rolebox--vu" : "rolebox"
                      }
                    >
                      {duty.slot}
                    </span>
                  </td>
                  <td>
                    <span
                      /* The house pills, unchanged: `--good` is the settled, finished state
                         everywhere in this app, and `--muted` is the neutral one. `Appointed`
                         is not a warning and must not be dressed as one. */
                      className={
                        duty.completed
                          ? "statuspill statuspill--good"
                          : "statuspill statuspill--muted"
                      }
                    >
                      {duty.completed ? "Completed" : "Appointed"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * How the two halves of a fixture are named: the team, or the position it will be filled from.
 *
 * The same rule, and the same fallback, as the Fixtures page and the Command Desk — a knockout
 * match TMS has not resolved yet is `6th Pool A`, and never a blank.
 */
function side(code: string | null, placeholder: string | null): string {
  return code ?? placeholder ?? "?";
}

/* ────────────────────────────── §B4 · Teams Coverage ────────────────────── */

export function TeamsCoveragePanel(): React.JSX.Element {
  return (
    <section className="panel panel--d2" aria-labelledby="covTitle" data-howto="teams-coverage">
      <div className="panel__head">
        <h3 className="panel__title" id="covTitle">
          Teams Coverage
        </h3>
        <span className="panel__note">Darker Means More Exposure</span>
        <span className="spacer" />
        <span className="filtlab">Competition</span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="covtable" aria-label="Teams this official has covered">
          <thead>
            <tr>
              <th scope="col">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="c-tot">0</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="panel__foot">
        <div className="matlegend" aria-label="Team exposure heat key" style={{ marginBottom: 6 }}>
          <span>Team Exposure</span>
          <span className="hsw">
            <i className="k0" aria-hidden="true" />0
          </span>
          <span className="hsw">
            <i className="k1" aria-hidden="true" />1
          </span>
          <span className="hsw">
            <i className="k2" aria-hidden="true" />2
          </span>
          <span className="hsw">
            <i className="k3" aria-hidden="true" />
            3+
          </span>
        </div>
        Each appointment covers <b>both</b> teams of that match, so the row total is twice the
        appointment count. This is §B4&rsquo;s own zero-row fallback — the header renders only{" "}
        <b>Total</b> and the body only <b>0</b> — because no appointment for this official is
        read on this page yet.
      </div>
    </section>
  );
}

/* ────────────────────────────── §B5 · Co-Officials Matrix ───────────────── */

export function CoOfficialsPanel(): React.JSX.Element {
  return (
    <section className="panel panel--d3" aria-labelledby="coTitle" data-howto="co-officials">
      <div className="panel__head">
        <h3 className="panel__title" id="coTitle">
          Co-Officials Matrix
        </h3>
        <span className="spacer" />
        <span className="panel__note">Sorted By Times Appointed Together</span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="mtable cotable" aria-label="Officials appointed alongside this official">
          <thead>
            <tr>
              <th scope="col">Official</th>
              <th scope="col">Country</th>
              <th scope="col" style={{ textAlign: "center" }}>
                Times
              </th>
              <th scope="col">Their Roles</th>
            </tr>
          </thead>
          <tbody />
        </table>
      </div>

      <EmptyState
        title="Nobody To Match Against Yet"
        detail="This matrix is derived from the same appointment crews as the table above, so it stays empty for exactly as long as that table does."
      />
    </section>
  );
}

/* ────────────────────────────── §B6 · Official Experience ───────────────── */

export function ExperiencePanel({
  official,
}: {
  official: OfficialDetailView;
}): React.JSX.Element {
  return (
    <section className="panel panel--d2" aria-labelledby="gradeTitle" data-howto="experience">
      <div className="panel__head">
        <h3 className="panel__title" id="gradeTitle">
          Official Experience
        </h3>
        <span className="spacer" />
        <span className="panel__note">Fetched From TMS</span>
      </div>

      <div style={{ padding: "14px 20px 0" }}>
        <span className="kicker">Current Gradings</span>
      </div>
      <ul className="gradelist" data-testid="gradings">
        <li>
          <span className="role">{official.role.toUpperCase()}</span>
          <span className="lvl">
            {official.gradingLevel ?? (
              <span className="cell-none" title="No grading row on this official's TMS record">
                No Grading Recorded
              </span>
            )}
          </span>
          <span className="src">TMS</span>
        </li>
      </ul>
      <div style={{ padding: "0 20px 14px" }}>
        <div className="hidenote">
          <b>Only the current tournament role is shown</b>
        </div>
      </div>

      <div style={{ padding: "4px 20px 0" }}>
        <span className="kicker">Caps</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="mtable capstable" aria-label="International caps recorded in TMS">
          <thead>
            <tr>
              <th scope="col">Age</th>
              <th scope="col">Discipline</th>
              <th scope="col">Gender</th>
              <th scope="col">Role</th>
              <th scope="col" title="Competitions">
                Comps
              </th>
              <th scope="col">Matches</th>
              <th scope="col" title="First Match">
                First
              </th>
              <th scope="col" title="Last Match">
                Last
              </th>
            </tr>
          </thead>
          <tbody>
            {official.caps.map((cap) => (
              <tr
                key={`${cap.role}-${cap.ageGroup}-${cap.discipline}-${cap.gender}`}
                data-testid="caps-row"
              >
                <td>{cap.ageGroup}</td>
                <td>{cap.discipline}</td>
                <td>{GENDER_WORD[cap.gender]}</td>
                <td>{cap.role}</td>
                <td className="num">{cap.competitions}</td>
                <td className="num" title={capTooltip(cap.role)}>
                  {cap.matches}
                </td>
                <td className="num">{capDate(cap.firstMatch)}</td>
                <td className="num">{capDate(cap.lastMatch)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {official.caps.length === 0 ? (
        <EmptyState
          title="No Caps Recorded"
          detail="TMS sent no cap rows for this official. That is not the same as a career of none — it is a record this system was never given, which is why the roster's Cap column reads — rather than 0."
        />
      ) : null}

      <div className="panel__foot">
        * For all games recorded in this system. The <b>Cap</b> column on the roster is the
        total of the rows above —{" "}
        <b>
          {official.cap === null ? "not recorded" : official.cap}
          {official.cap === null || official.capUnit === null
            ? ""
            : ` ${CAP_UNIT_SHORT[official.capUnit]}`}
        </b>
        {" "}({capTooltip(official.role).toLowerCase()}).{" "}
        <b>Role here is the role the caps were earned in</b>, which need not be the role held at
        this tournament, and a blank <b>First</b> or <b>Last</b> is a date TMS does not carry
        rather than a match that never happened.
      </div>
    </section>
  );
}

/* ────────────────────────────── The one notes read, shared (run 51) ─────── */

/**
 * The TD's notes on one official, fetched ONCE for the page — run 51.
 *
 * Three panels now read the same stream: `Quick Notes` lists it, `TD Feedback` groups it by
 * grading category, and `Report Readiness` counts the categories it covers. Fetching it three
 * times would be three chances for three panels to disagree on one screen, so the page owns the
 * read and hands the state down.
 *
 * **The edit overlay lives here for the same reason.** A correction saved in `Quick Notes` can
 * move a note from one category to another; if the overlay stayed inside that panel, the
 * feedback grouping and the ring beside it would still be drawing the note's old category until
 * the next reload. {@link TdNotesState.applyEdit} lays the saved note over the fetched list for
 * every reader at once.
 */
export interface TdNotesState {
  status: "loading" | "error" | "ready";
  /** Set only in the `error` state; the banner's own input. */
  error: Error | null;
  /** The fetched notes with any in-session corrections laid over them. Empty until ready. */
  notes: readonly TdNoteView[];
  /** What a successful `PUT /td-notes/:id` returned, for every panel reading this state. */
  applyEdit: (saved: TdNoteView) => void;
}

export function useTdNotes(tmsPersonId: string): TdNotesState {
  const fetched = useAsync(() => fetchTdNotes(tmsPersonId), [tmsPersonId]);
  // Keyed by note id — what a save returned, laid back over the fetched list so the panels
  // "refresh" to the re-tidied result without a second round trip to the server.
  const [edited, setEdited] = useState<Record<string, TdNoteView>>({});

  // A different official is a different notebook: an edit from the previous one must not
  // survive the move and reappear over somebody else's note.
  useEffect(() => {
    setEdited({});
  }, [tmsPersonId]);

  return {
    status: fetched.status,
    error: fetched.status === "error" ? fetched.error : null,
    notes: fetched.status === "ready" ? fetched.data.notes.map((n) => edited[n.id] ?? n) : [],
    applyEdit: (saved) => {
      setEdited((current) => ({ ...current, [saved.id]: saved }));
    },
  };
}

/* ────────────────────────────── §B11/§B10 · coverage arithmetic (run 51) ── */

/** One category's worth of feedback — a grading criterion, or `General`. */
export interface TdFeedbackGroup {
  /** The criterion's stable key, or `null` for `General`. */
  key: string | null;
  /** The category as a human reads it — tidied, never the stable key. */
  label: string;
  notes: readonly TdNoteView[];
  /**
   * True where this category is one of the role's grading criteria, and so counts towards
   * coverage. `General` is false: it is a real category a TD chooses, but it is not a category
   * he is marking against, so a notebook full of General notes has covered nothing.
   */
  grading: boolean;
}

export interface TdFeedbackCoverage {
  /** Every note, `General` included — the `N Entries` figure. */
  entries: number;
  /** Grading categories holding at least one note — the readiness numerator. */
  covered: number;
  /** The role's grading criteria count — the readiness denominator. */
  total: number;
  /** Groups that hold notes, in the role's criterion order, `General` last. */
  groups: readonly TdFeedbackGroup[];
  /** Grading categories with nothing recorded against them yet, in the same order. */
  uncovered: readonly string[];
}

/**
 * The arithmetic both §B11 and §B10 print — run 51.
 *
 * **`General` counts as an entry and never as coverage.** It is the absence of a criterion (see
 * `TD_NOTE_GENERAL_LABEL` in `@hockeytd/shared`), so a note filed there is evidence about the
 * official at large and cannot be evidence *for a mark* on a category nobody chose. Both panels
 * say so in their foot, because `7 Entries · 3 Of 8 Categories Covered` is otherwise arithmetic
 * the reader has to guess at.
 *
 * **A key outside the role's set keeps its own group and is not counted either.** No route can
 * store one today — the picker is role-scoped and the wire check is the union of all three sets
 * — but a note captured before a role change would land here, and quietly folding it into
 * `General` would relabel the TD's own filing. It is drawn under its own name, below the role's
 * categories, and left out of a denominator it is not part of.
 */
export function tdFeedbackCoverage(
  notes: readonly TdNoteView[],
  role: OfficialRole,
): TdFeedbackCoverage {
  const criteria = tdNoteCategoriesForRole(role);
  const byKey = new Map<string | null, TdNoteView[]>();
  for (const note of notes) {
    const held = byKey.get(note.criterionStableKey);
    if (held === undefined) byKey.set(note.criterionStableKey, [note]);
    else held.push(note);
  }

  const ofRole = new Set(criteria.map((criterion) => criterion.criterion_stable_key));
  const grading: TdFeedbackGroup[] = criteria.map((criterion) => ({
    key: criterion.criterion_stable_key,
    label: tidyCriterionDisplayName(criterion.name),
    notes: byKey.get(criterion.criterion_stable_key) ?? [],
    grading: true,
  }));
  const foreign: TdFeedbackGroup[] = [...byKey.keys()]
    .filter((key): key is string => key !== null && !ofRole.has(key))
    .map((key) => ({
      key,
      label: tdNoteCategoryLabel(key),
      notes: byKey.get(key) ?? [],
      grading: false,
    }));
  const general: TdFeedbackGroup = {
    key: null,
    label: TD_NOTE_GENERAL_LABEL,
    notes: byKey.get(null) ?? [],
    grading: false,
  };

  return {
    entries: notes.length,
    covered: grading.filter((group) => group.notes.length > 0).length,
    total: criteria.length,
    groups: [...grading, ...foreign, general].filter((group) => group.notes.length > 0),
    uncovered: grading
      .filter((group) => group.notes.length === 0)
      .map((group) => group.label),
  };
}

/* ────────────────────────────── §B7 · Quick Notes ───────────────────────── */

/**
 * §B7 prints a capture time as `YYYY-MM-DD HH:MM`. The stored value is an ISO instant.
 *
 * **RUN 83 — on the tournament's clock.** It was `getFullYear()`/`getHours()` and their
 * siblings, which cut the instant up in the DEVICE's zone. A note the TD typed courtside during
 * a match was stamped with a time that had nothing to do with the match, and on a laptop west of
 * the date line with the wrong day beside it — on the one panel whose purpose is to tell him
 * when he wrote what. An unparseable instant is still returned exactly as it came.
 */
export function quickNoteWhen(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return tournamentDayAndClock(at);
}

/** §B7's pagination — five to a page, newest first. */
export const QUICK_NOTES_PER_PAGE = 5;

/**
 * One note's `Show As Typed` affordance — run 45.
 *
 * From run 45 a quick note is tidied and, where it is not in English, translated on save; the
 * English is the record and the text as typed is kept beside it (`td_note.body_original`,
 * migration 043). This is where the Technical Delegate gets his own words back.
 *
 * **It is drawn only when the two differ**, which is exactly when there is something to show.
 * A note that saved as typed — nothing tidied it, or the call failed — has one text, and a
 * disclosure control that revealed the identical sentence would be noise on every row of a
 * notebook whose ordinary case is a clean English note.
 *
 * **Unobtrusive, per §B7's reading discipline.** A `linkbtn` in the note's own line, collapsed
 * by default, revealing the original beneath. Not a second permanent column: the panel's job is
 * to let the TD read his notes, and two versions of every note side by side would halve how
 * many he can take in at a glance for a comparison he wants on almost none of them.
 */
function NoteAsTyped({ note }: { note: TdNoteView }): React.JSX.Element | null {
  const [shown, setShown] = useState(false);
  if (note.bodyOriginal === note.body) return null;

  return (
    <>
      <button
        type="button"
        className="linkbtn"
        data-testid="quick-note-as-typed"
        aria-expanded={shown}
        title="This note was tidied, or translated into English, when it was saved. This shows what you typed."
        onClick={() => {
          setShown(!shown);
        }}
      >
        {shown ? "Hide Original" : "As Typed"}
      </button>
      {shown ? (
        <span className="txt qnorig" data-testid="quick-note-original">
          <b>As typed —</b> {note.bodyOriginal}
        </span>
      ) : null}
    </>
  );
}

/**
 * The Category select shared by the edit-in-place form and the pitch-side capture card — the
 * same option set (that official's own marking set, plus General) and the same tidied labels,
 * so a note corrected here is filed under a category the marking screen would recognise too.
 */
const EDIT_GENERAL = "";

/**
 * Edit-in-place for one Quick Note — run 48.
 *
 * **Why the profile grows this and not a second capture box.** §B7's standing rule is that the
 * profile is where notes are READ, not composed — see {@link QuickNotesPanel}'s own header —
 * and that rule is about CAPTURE: a fresh observation is typed pitch-side, at the moment it
 * happens, against the roster the capture card offers. A CORRECTION is a different act. Alfred's
 * ruling (2026-09-03): a Technical Delegate reading a note back is exactly the moment a typo
 * from fast pitch-side typing is caught, and asking him to remember it and walk back to the
 * Command Desk to fix it is asking him to lose the correction. So this is amendment of an
 * existing observation, on the id it already has — the same `PUT /td-notes/:id` the capture card
 * calls, replayed with the author's corrected words — never a new note and never a second author.
 *
 * **Opens on the ENGLISH record, not the words last typed.** `note.body` is what every surface
 * reads and what the TD is rereading when he spots the slip, so that is what a save should
 * amend. `bodyOriginal` remains reachable via `As Typed` on the read row underneath, unaffected
 * until this form is actually saved.
 *
 * **Save re-runs the run-45 AI pass on the NEW text**, because the route treats every `PUT` on
 * an id alike — see `tidiedNote` and the header of `routes/td-notes.ts` §"the original tracks
 * the note, edit by edit". The panel does nothing to keep that true; it draws what the write
 * returns.
 */
function QuickNoteEditor({
  note,
  role,
  onCancel,
  onSaved,
}: {
  note: TdNoteView;
  role: OfficialRole;
  onCancel: () => void;
  onSaved: (saved: TdNoteView) => void;
}): React.JSX.Element {
  const [body, setBody] = useState(note.body);
  const [category, setCategory] = useState(note.criterionStableKey ?? EDIT_GENERAL);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const categories = tdNoteCategoriesForRole(role);

  const save = (): void => {
    const trimmed = body.trim();
    if (trimmed === "") {
      setFailure("The note cannot be saved empty — cancel instead if it should stay as it was.");
      return;
    }
    setSaving(true);
    setFailure(null);
    putTdNote(note.id, {
      subjectTmsPersonId: note.subjectTmsPersonId,
      body: trimmed,
      // General is the absence of a criterion — the same null the capture card sends. See
      // TdQuickNoteCapture's header.
      criterionStableKey: category === EDIT_GENERAL ? null : category,
    }).then(
      (result) => {
        setSaving(false);
        onSaved(result.note);
      },
      (error: unknown) => {
        setSaving(false);
        setFailure(error instanceof Error ? error.message : "The note could not be saved.");
      },
    );
  };

  return (
    <span className="qnedit dutyeditor" data-testid="quick-note-edit-editor">
      <label className="qnedit__category">
        Category
        <select
          value={category}
          disabled={saving}
          data-testid="quick-note-edit-category"
          onChange={(event) => {
            setCategory(event.target.value);
            setFailure(null);
          }}
        >
          <option value={EDIT_GENERAL}>{TD_NOTE_GENERAL_LABEL}</option>
          {categories.map((criterion) => (
            <option key={criterion.criterion_stable_key} value={criterion.criterion_stable_key}>
              {tidyCriterionDisplayName(criterion.name)}
            </option>
          ))}
        </select>
      </label>

      <textarea
        className="qnedit__body notesarea"
        aria-label="Edit quick note"
        value={body}
        disabled={saving}
        data-testid="quick-note-edit-body"
        onChange={(event) => {
          setBody(event.target.value);
          setFailure(null);
        }}
      />

      <span className="dutyactions">
        <button
          type="button"
          className="linkbtn"
          disabled={saving}
          data-testid="quick-note-edit-save"
          onClick={save}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="linkbtn"
          disabled={saving}
          data-testid="quick-note-edit-cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
      </span>

      {failure === null ? null : (
        <span className="dutyfail" role="alert" data-testid="quick-note-edit-fail">
          {failure}
        </span>
      )}
    </span>
  );
}

/**
 * §B7 `Quick Notes` — WIRED, and, from run 48, editable in place.
 *
 * **What changed in run 30.** Until now this panel said, in words, that it read no note route
 * at all, because there was no store for the TD's own technical-officials notes. There is one
 * now — `td_note`, migration 037, the separate store `um-app/00-overview.md` §2 requires and
 * that `routes/notes.ts` refuses a TD write in favour of — so the panel reads it.
 *
 * **What still does NOT change.** §B7 is explicit that the profile is where notes are read and
 * not where they are composed, and that stands: there is still no add-note button and no delete
 * affordance here (`deleteTdNote` exists on the client for a future run — see run 48's report).
 * What run 48 adds is narrower than either — a correction of a note the TD has already written,
 * on its own id, because rereading a note pitch-side is exactly when a typo is caught and asking
 * him to walk back to the Command Desk to fix it would cost the correction. See
 * {@link QuickNoteEditor}'s header for the ruling this reads from.
 *
 * **Ruling R-4′ is untouched.** This reads and amends the TD's OWN stream — his notes, on his
 * screen, about technical officials. It is not the UM stream, it carries no count about any UM,
 * and no signal about anybody's unreleased notes can reach this page through it.
 *
 * The empty state is honest about which of the two empties it is: there is a store now, and it
 * holds nothing for this official.
 *
 * **What changed in run 45.** A quick note is tidied of dictation artefacts on save and, where
 * it is not in English, translated — so `note.body` is now the English **record** rather than
 * necessarily the words that were typed. The words that were typed are kept
 * (`td_note.body_original`, migration 043) and {@link NoteAsTyped} is how the Technical
 * Delegate reads them back. An edit here re-tidies the new text the same way a fresh capture
 * does, and rewrites `body_original` to what was just typed — see `routes/td-notes.ts`.
 */
export function QuickNotesPanel({
  tmsPersonId,
  role,
  notes,
}: {
  /** The official whose notes these are. The route filters server-side; this is the key. */
  tmsPersonId: string;
  /** Whose marking set the category picker offers — the same set the capture card uses. */
  role: OfficialRole;
  /**
   * The page's one notes read (run 51) — shared with `TD Feedback` and `Report Readiness`, so
   * a correction saved here moves the grouped feedback and the readiness ring with it.
   */
  notes: TdNotesState;
}): React.JSX.Element {
  const [page, setPage] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);

  // A different official is a different notebook, so the pager returns to the first page
  // rather than leaving the reader on page three of somebody with one note, and a half-open
  // editor does not survive the move.
  useEffect(() => {
    setPage(0);
    setEditingId(null);
  }, [tmsPersonId]);

  const all = notes.notes;
  const pages = Math.max(1, Math.ceil(all.length / QUICK_NOTES_PER_PAGE));
  const current = Math.min(page, pages - 1);
  const shown = all.slice(current * QUICK_NOTES_PER_PAGE, (current + 1) * QUICK_NOTES_PER_PAGE);

  return (
    <section className="panel panel--d4" aria-labelledby="qnTitle" data-howto="quick-notes">
      <div className="panel__head">
        <h3 className="panel__title" id="qnTitle">
          Quick Notes
        </h3>
        <span className="spacer" />
        <span className="panel__note">Your Own Notes · Newest First</span>
      </div>

      {notes.status === "loading" ? <Skeleton rows={3} /> : null}
      {notes.error === null ? null : <FailBanner error={notes.error} />}

      {notes.status === "ready" && all.length === 0 ? (
        <EmptyState
          title="No Notes Yet For This Official"
          detail="Quick notes are captured pitch-side and read here. Nothing has been written about this official yet — which is a fact about the notebook, not about the person."
        />
      ) : null}

      {shown.length === 0 ? null : (
        <ul className="qnlist" data-testid="quick-notes-list">
          {shown.map((note) =>
            editingId === note.id ? (
              <li className="qnitem qnitem--editing" key={note.id}>
                <span className="when">{quickNoteWhen(note.createdAt)}</span>
                {note.matchId === null ? null : (
                  <span className="cat" title="The match this note was captured at">
                    Match {note.matchId}
                  </span>
                )}
                <QuickNoteEditor
                  note={note}
                  role={role}
                  onCancel={() => {
                    setEditingId(null);
                  }}
                  onSaved={(saved) => {
                    notes.applyEdit(saved);
                    setEditingId(null);
                  }}
                />
              </li>
            ) : (
              <li className="qnitem" key={note.id}>
                <span className="when">{quickNoteWhen(note.createdAt)}</span>
                {note.matchId === null ? null : (
                  <span className="cat" title="The match this note was captured at">
                    Match {note.matchId}
                  </span>
                )}
                {/*
                 * §B7's `span.cat`, and it is drawn for EVERY note rather than only for a
                 * categorised one. A note with no criterion is not a note missing something —
                 * `General` is a category the TD chose from the picker, and the same null is
                 * what every note written before that picker existed carries, so the two read
                 * alike and neither looks unfinished beside a criterion note.
                 */}
                <span className="cat" title="The grading category this note is filed under">
                  {tdNoteCategoryLabel(note.criterionStableKey)}
                </span>
                <span className="txt">{note.body}</span>
                <NoteAsTyped note={note} />
                {/*
                 * Run 48. Amendment, not composition — see {@link QuickNoteEditor}'s header.
                 * One row edits at a time: opening a second editor while one is unsaved would
                 * leave the TD wondering which Save button applies to which note.
                 */}
                <button
                  type="button"
                  className="linkbtn qnitem__edit"
                  data-testid="quick-note-edit"
                  onClick={() => {
                    setEditingId(note.id);
                  }}
                >
                  Edit
                </button>
              </li>
            ),
          )}
        </ul>
      )}

      {all.length > QUICK_NOTES_PER_PAGE ? (
        <div className="qnpager" data-testid="quick-notes-pager">
          <button
            type="button"
            className="linkbtn"
            aria-label="Previous page"
            disabled={current === 0}
            onClick={() => {
              setPage(current - 1);
            }}
          >
            &lsaquo; Prev
          </button>
          <span className="qnpager__meta">
            Page {current + 1} Of {pages} · {all.length} Notes
          </span>
          <button
            type="button"
            className="linkbtn"
            aria-label="Next page"
            disabled={current >= pages - 1}
            onClick={() => {
              setPage(current + 1);
            }}
          >
            Next &rsaquo;
          </button>
        </div>
      ) : null}

      <div className="panel__foot">
        <b>Read here, written pitch-side.</b> New notes are still captured pitch-side and not on
        this panel — there is deliberately no add-note or dictate button here. But reading a note
        back is exactly the moment a typo from fast typing at the side of a pitch gets caught, so{" "}
        <b>Edit</b> lets you correct one you have already written, on the spot. These are the
        Technical Delegate&rsquo;s own notes on technical officials — a separate stream from the
        Umpires Managers&rsquo;, which never mixes with it — and they appear on no outbound or
        printed document.
        {" "}
        <b>Tidied on save.</b> A note is cleaned up, and translated into English where it needs
        to be, at the moment it is captured; what you see here is that English record. Where it
        differs from what you typed, <b>As Typed</b> shows your own words — they are kept on
        every note and are never lost, including when the tidying fails.
      </div>
    </section>
  );
}

/* ────────────────────────────── §B8 · Cards Issued (umpires only) ───────── */

export function CardsIssuedPanel(): React.JSX.Element {
  return (
    <section className="panel panel--d4" aria-labelledby="cardsTitle" data-testid="cards-issued">
      <div className="panel__head">
        <h3 className="panel__title" id="cardsTitle">
          Cards Issued
        </h3>
        <span className="spacer" />
        <span className="panel__note">Umpires Only · This Tournament</span>
      </div>

      <div className="cardsgrid">
        <span className="cardstat">
          <svg className="cardicon" viewBox="0 0 16 16" aria-hidden="true">
            <polygon points="8,1 15,15 1,15" fill="#1d7a46" />
          </svg>
          <b>—</b>
          <span className="lbl">Green</span>
        </span>
        <span className="cardstat">
          <svg className="cardicon" viewBox="0 0 16 16" aria-hidden="true">
            <rect x="1.5" y="1.5" width="13" height="13" rx="1.5" fill="#d3a80c" />
          </svg>
          <b>—</b>
          <span className="lbl">Yellow</span>
        </span>
        <span className="cardstat">
          <svg className="cardicon" viewBox="0 0 16 16" aria-hidden="true">
            <circle cx="8" cy="8" r="7" fill="#b3261e" />
          </svg>
          <b>—</b>
          <span className="lbl">Red</span>
        </span>
        <span className="cardstat cardstat--tot">
          <b>—</b>
          <span className="lbl">Total</span>
        </span>
      </div>

      <div className="panel__foot">
        <b>Umpire-only panel —</b> cards are issued by on-field umpires, so this panel is
        rendered only when the official&rsquo;s tournament role is Umpire. For a Technical
        Officer, Judge, ATD or Umpires Manager it is hidden entirely rather than shown empty.
        The counts read <b>—</b> and not <b>0</b>: no card store is read in this build, and a
        zero would be a claim that this umpire issued none.
      </div>
    </section>
  );
}

/* ────────────────────────────── §B9 · Past Experience / Expectation ─────── */

/**
 * The intake answers carry `**markdown-style bold**`, and this is the whole of what is done
 * about it.
 *
 * **Why they carry it at all.** The intake e-mail asks eight numbered questions and officials
 * answer them in the client they happen to be using; what arrives is plain text in which
 * somebody has starred the part they wanted to stress — a tournament name, a role, the one
 * sentence they actually wanted the Technical Delegate to read. The import stores the reply as
 * it was written, markers and all, because the reply is the record. Printing `**` on the
 * profile turns a person's emphasis into punctuation nobody typed.
 *
 * **A splitter, and deliberately not a markdown library.** Two asterisks are the only marker
 * these replies use. A parser would also give us headings, links, images and raw HTML from
 * text an outside party e-mailed in — an injection surface bought to solve a problem that is
 * one `String.split` wide. Everything that is not `**…**` stays exactly as it was typed,
 * including a lone asterisk, an underscore and a `<`, all of which React escapes for us.
 *
 * **An unmatched marker is printed, not swallowed.** A reply ending `**most of all` has opened
 * an emphasis it never closed; bolding the remainder would be this component deciding what the
 * writer meant, and dropping the `**` would be it silently editing them. The marker is put
 * back and the text reads as it arrived.
 */
export function intakeEmphasis(text: string): React.JSX.Element[] {
  const parts = text.split("**");

  // An even number of pieces means an ODD number of markers, so the last one closes nothing.
  // Re-attach it to the piece before it and let it print as the two characters it is.
  if (parts.length % 2 === 0) {
    const tail = parts.pop() ?? "";
    parts[parts.length - 1] = `${parts[parts.length - 1] ?? ""}**${tail}`;
  }

  // Odd indices sat BETWEEN a matched pair of markers; even indices sat outside every pair.
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <strong key={index}>{part}</strong>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

/**
 * §B9's `Replied <date>` — the day the matched intake submission arrived, or null where none
 * has been.
 *
 * **The day and not the minute.** `Quick Notes` stamps a time because the TD wants to know
 * when in a match he wrote something; an intake reply is correspondence, and `14:07` on it
 * would be precision about a fact nobody reads at that resolution.
 *
 * On the TOURNAMENT's clock, like {@link quickNoteWhen} and for its reason: an instant cut up
 * in the device's zone dates a reply to the wrong day on a laptop west of the date line. An
 * instant that will not parse yields null rather than `Invalid Date` in the panel head.
 */
export function intakeRepliedOn(instant: string | null): string | null {
  if (instant === null) return null;
  const at = new Date(instant);
  return Number.isNaN(at.getTime()) ? null : tournamentDay(at);
}

/**
 * One block of the §B9 grid — a heading, a chip, the text, and the line saying where it came
 * from.
 *
 * **An absent answer is named, never blank.** `No Intake Reply Received` is the same honest
 * `cell-none` the profile card uses for a missing mobile number: it says the reply is not
 * there, as against saying nothing and leaving the Technical Delegate to wonder whether the
 * panel is broken or the official is silent.
 *
 * `white-space: pre-wrap` because these are two or three sentences somebody typed into an
 * e-mail, and the paragraph breaks they typed are part of what they wrote.
 */
function IntakeAnswerBlock({
  kicker,
  text,
  testId,
}: {
  kicker: string;
  text: string | null;
  testId: string;
}): React.JSX.Element {
  const answered = text !== null && text.trim() !== "";
  return (
    <div className="expblock">
      <div className="exphead">
        <span className="kicker">{kicker}</span>
        <span className="expchip">From Email Reply</span>
      </div>
      {/*
       * `intakeEmphasis` rather than the bare string: the reply is stored as it was written,
       * and what was written carries `**…**` around the part the official wanted stressed.
       * `text!` is safe under `answered`, which is exactly the null-and-blank test.
       */}
      <div className="txt" data-testid={testId} style={{ whiteSpace: "pre-wrap" }}>
        {answered ? (
          intakeEmphasis(text as string)
        ) : (
          <span className="cell-none">No Intake Reply Received</span>
        )}
      </div>
      <div className="src">
        {answered
          ? "From The Official's Reply To The Intake Email"
          : "From The Intake Reply · No Reply Recorded For This Official"}
      </div>
    </div>
  );
}

/**
 * §B9's `TD Expectation` box — the Technical Delegate's own meeting record, and the one
 * editable field on this panel.
 *
 * **The state it keeps is the state a failing save must not destroy.** `text` is the note as it
 * currently reads in the box and is the single source of what is drawn; `saved` is the note as
 * the database last confirmed it. Dirtiness is the difference between the two, which means a
 * refused save leaves the box dirty and the typed text intact by construction rather than by a
 * remembering-to error path. Nothing in this component assigns `text` from anything but the
 * TD's own typing and a 2xx.
 *
 * **Seeded once, on mount.** The panel gives this a `key` of the official's TMS person id, so
 * moving to another profile builds a new editor around that person's stored note instead of
 * carrying a half-typed paragraph across two people's records. Within one profile the box is
 * never re-seeded from a re-render: a fetch completing elsewhere on the page must not rewrite
 * what he is in the middle of typing.
 *
 * **`Saved` is a flash and `Unsaved changes` is not.** The confirmation is a fact about a moment
 * — a write returned — and it stands down after {@link SAVED_FLASH_MS} so that a box left open
 * all afternoon does not go on claiming a save that happened before lunch. Dirtiness is a fact
 * about the text on screen and stays true for exactly as long as it is.
 *
 * **Empty is a save, not a refusal.** Clearing the box is how the TD withdraws a note he should
 * not have made; the route stores an empty note as NULL and answers with `null`, and `saved`
 * takes that as `""`. So clearing and saving leaves a clean, empty box rather than a permanently
 * dirty one.
 */
function TdExpectationEditor({
  official,
}: {
  official: OfficialDetailView;
}): React.JSX.Element {
  const stored = official.tdExpectation ?? "";
  const [text, setText] = useState(stored);
  const [saved, setSaved] = useState(stored);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const dirty = text !== saved;

  // The flash stands down on its own; the timer is cleared if the box is saved again first, so
  // a second save cannot be cancelled by the first one's countdown.
  useEffect(() => {
    if (!confirmed) return undefined;
    const timer = setTimeout(() => void setConfirmed(false), SAVED_FLASH_MS);
    return () => void clearTimeout(timer);
  }, [confirmed]);

  const save = (): void => {
    setSaving(true);
    setConfirmed(false);
    setFailure(null);
    putOfficialTdExpectation(official.tmsPersonId, text).then(
      (result) => {
        setSaving(false);
        // From the ANSWER, not from `text`: an emptied note stores as NULL, and reseeding from
        // what was sent would leave this box holding `""` while the profile says `null`.
        setSaved(result.tdExpectation ?? "");
        setConfirmed(true);
      },
      (error: unknown) => {
        setSaving(false);
        // `text` is deliberately untouched. Everything the Technical Delegate typed stays on
        // the screen, stays dirty, and stays one press of the same button away from being
        // saved — see the panel header for why that is this field's whole history.
        setFailure(
          error instanceof Error
            ? error.message
            : "The note could not be saved. Nothing you typed has been lost.",
        );
      },
    );
  };

  return (
    <>
      <textarea
        className="notesarea"
        aria-label="TD Expectation"
        data-testid="td-expectation"
        value={text}
        disabled={saving}
        onChange={(event) => {
          setText(event.target.value);
          // The previous outcome is about the previous text and stops being true the moment a
          // key is pressed. Both are cleared rather than left to be read against new words.
          setConfirmed(false);
          setFailure(null);
        }}
        placeholder="Conversation details from your personal meeting(s) with this official — what you expect from them this tournament, agreed focus points, follow-ups."
      />
      <div className="aibar">
        <button
          type="button"
          className="linkbtn"
          // Not dirty means the database already holds this: a round trip to store what is
          // stored is a round trip that can only fail. In flight means the answer is still
          // coming, and a second press on a venue connection would be a second write.
          disabled={saving || !dirty}
          data-testid="td-expectation-save"
          onClick={save}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <span className="tdexp__state" role="status" data-testid="td-expectation-state">
          {saving
            ? "Saving…"
            : dirty
              ? "Unsaved changes"
              : confirmed
                ? "Saved"
                : ""}
        </span>
        <span className="btnhint">
          Yours alone — your record of your meetings with this official, saved to their profile
          and shown to nobody else. It is never printed on an outbound document.
        </span>
      </div>
      {failure === null ? null : (
        <span className="dutyfail" role="alert" data-testid="td-expectation-fail">
          {failure} Nothing you typed has been lost — press Save again when you are ready.
        </span>
      )}
    </>
  );
}

/** How long the `Saved` confirmation stands before it stands down. See the editor's header. */
const SAVED_FLASH_MS = 2600;

/**
 * §B9 — the two intake answers and the TD's own note, all three now drawn from the record.
 *
 * **What changed in run 27.** Until the version 2 bootstrap bundle landed there was nowhere to
 * keep an intake reply, so this panel drew three empty blocks and said so. The bundle now
 * carries the officials' answers to intake questions 7 and 8, the importer writes them, and
 * the `requireTd`-guarded profile projection carries them here. The panel reads what is
 * actually stored: the answers where they exist, and the named absence where they do not.
 *
 * **What changed since.** Two things, both about reading what is already there rather than
 * fetching anything new:
 *
 *   · The stored answers carry `**markdown-style bold**`, because that is how the officials
 *     typed them and the import keeps a reply as it was written. Until now the asterisks were
 *     printed, so a person's emphasis reached the Technical Delegate as punctuation. They are
 *     rendered — see {@link intakeEmphasis}, which is a splitter and not a parser.
 *   · The panel head says `Replied <date>` when a matched `intake_submission` dates the reply.
 *     It is the difference between a block that is empty because the official skipped question
 *     8 and one that is empty because nobody has heard from them at all, and it tells the TD
 *     whether what he is reading was written before the draw or the week play started.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TD EXPECTATION — WRITABLE NOW, AND STILL TD-ONLY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **It was read-only, and the reason it was read-only is the reason the write path is shaped
 * the way it is.** `official.td_expectation` has been in the schema since migration 003 and had
 * no writer: the importer is forbidden from touching it and no route updated it, so a Save
 * button here would have taken a paragraph of the Technical Delegate's meeting notes and thrown
 * it away on every press — and would have looked as though it had worked. The panel said so on
 * the page rather than offering a button that lied.
 *
 * `PUT /officials/:tmsPersonId/td-expectation` is that writer, and the promise the read-only
 * state was keeping is now kept by {@link TdExpectationEditor} instead:
 *
 *   · The box holds LOCAL state, seeded from the projection. It is never re-seeded from
 *     anything but a successful save, so nothing a network can do re-renders the TD's
 *     half-written paragraph out from under him.
 *   · A refused save **keeps every character he typed**, says what went wrong beside the box,
 *     and leaves the state dirty so the same Save button is still the way out. There is no
 *     path in this component on which typed text is discarded — that is the whole inheritance
 *     from the read-only build and it is asserted by test.
 *   · Save is disabled while a write is in flight and when nothing has changed, so a double
 *     tap on a venue connection is one write and an idle button is not an invitation to make a
 *     round trip that would store what is already stored.
 *   · The indicator says which of the three states the box is in — `Unsaved changes`, `Saving…`
 *     or `Saved` — because a text box that silently agrees with the database looks exactly like
 *     one that silently does not.
 *
 * **The editor reseeds from what the SERVER returned**, not from what it sent. The one place
 * the two differ is an emptied note, which stores as NULL; reseeding from the request would
 * leave this panel holding `""` while the profile projection says `null`, and the difference
 * would surface later as a phantom unsaved change.
 *
 * **TD-only is unchanged, and the write path did not widen it by one reader.** It is his record
 * of his own meetings: it travels on this projection and no other; the roster list strips it;
 * the route that writes it carries the same `requireTd` as the route that reads it; and **no
 * generated document reads it**, which is what migration 003's comment on the column has always
 * meant. Showing the TD his own note on his own screen is not printing it on a letter, and
 * neither is letting him type it there.
 */
export function PastExperiencePanel({
  official,
}: {
  official: OfficialDetailView;
}): React.JSX.Element {
  const repliedOn = intakeRepliedOn(official.intakeReceivedAt);

  return (
    <section className="panel panel--d4" aria-labelledby="expTitle" data-howto="past-experience">
      <div className="panel__head">
        <h3 className="panel__title" id="expTitle">
          Past Experience / Expectation
        </h3>
        <span className="spacer" />
        {/*
         * The note gains `Replied <date>` only when a submission has actually been matched.
         * There is no "not replied" half to this: the two blocks below already name the
         * absence where it exists, and a head that said so a third time would be shouting.
         */}
        <span className="panel__note" data-testid="intake-replied">
          From The Intake Email Reply · TD&rsquo;s Own Notes
          {repliedOn === null ? null : ` · Replied ${repliedOn}`}
        </span>
      </div>

      <div className="expgrid">
        <div className="expcol">
          <IntakeAnswerBlock
            kicker="Past Experience"
            text={official.pastExperience}
            testId="past-experience"
          />
          <IntakeAnswerBlock
            kicker="Expectation Of Tournament / TD"
            text={official.expectationOfTournament}
            testId="expectation-of-tournament"
          />
        </div>

        <div className="expcol">
          <div className="expblock">
            <div className="exphead">
              <span className="kicker">TD Expectation</span>
              <span className="expchip expchip--td">TD&rsquo;s Own Notes</span>
            </div>
            {/*
             * Keyed on the person, so moving to another profile builds a new editor around
             * that person's stored note rather than carrying a half-typed paragraph across two
             * officials' records. See {@link TdExpectationEditor} for why the box is seeded
             * once and never re-seeded by a re-render.
             */}
            <TdExpectationEditor key={official.tmsPersonId} official={official} />
          </div>
        </div>
      </div>

      <div className="panel__foot">
        <b>Where these come from —</b> Past Experience and Expectation Of Tournament / TD are
        the official&rsquo;s own answers to questions 7 and 8 of the intake email, imported with
        the tournament bundle; where an official has not replied, the block says so rather than
        sitting empty. <b>TD Expectation</b> is yours to write — type it and press Save, adding
        to it across the tournament as you meet this official again. It is stored on their
        profile, is shown to nobody else, and is never printed on outbound documents.
      </div>
    </section>
  );
}

/* ────────────────────────────── §B10 · Report Readiness ─────────────────── */

/** The readiness ring's geometry — `viewBox` 120, stroke 9 (see `.readyring` in the CSS). */
const READY_RADIUS = 50;
const READY_CIRCUMFERENCE = 2 * Math.PI * READY_RADIUS;

/**
 * §B10 `Report Readiness` — WIRED, run 51.
 *
 * **The numerator exists now.** Until today this panel drew an empty ring and an em dash,
 * because §B10 forbids `0%` where the figure is unknown — *"it reads as a real finding"* — and
 * there was no feedback store to count. `td_note` is that store: the numerator is the grading
 * categories holding at least one quick note, the denominator is the role's criteria set, and
 * the arc is drawn to the fraction between them.
 *
 * **So `0%` is now drawn, and is now correct.** The old rule was never about the digit; it was
 * about asserting a finding the system could not know. With a real store behind it, `0 of 8
 * categories covered` is the true, useful and slightly uncomfortable fact a TD about to draft
 * ought to see, and the missing categories are named beside it rather than merely counted.
 *
 * **Two honest not-a-percentage cases remain.** A read still in flight draws the skeleton, and a
 * role with no marking set at all (the TD himself, the Tournament Administrator — ruling R2′)
 * has no denominator, so the ring stays empty and the panel says which of the two it is.
 */
export function ReportReadinessPanel({
  role,
  notes,
}: {
  /** Whose criteria set is the denominator, or null while the official is still loading. */
  role: OfficialRole | null;
  /** The page's one notes read — see {@link useTdNotes}. */
  notes: TdNotesState;
}): React.JSX.Element {
  const coverage = role === null ? null : tdFeedbackCoverage(notes.notes, role);
  const measurable = coverage !== null && notes.status === "ready" && coverage.total > 0;
  const percent =
    coverage === null
      ? 0
      : Math.round(readinessFraction({ covered: coverage.covered, total: coverage.total }) * 100);
  const filled =
    coverage === null
      ? 0
      : READY_CIRCUMFERENCE * readinessFraction({ covered: coverage.covered, total: coverage.total });

  return (
    <section className="panel panel--d4" aria-labelledby="readyTitle" data-howto="report-readiness">
      <div className="panel__head">
        <h3 className="panel__title" id="readyTitle">
          Report Readiness
        </h3>
        <span className="spacer" />
        <span className="panel__note">Grading Categories With Recorded Feedback</span>
      </div>

      {notes.status === "loading" || role === null ? <Skeleton rows={3} /> : null}
      {notes.error === null ? null : <FailBanner error={notes.error} />}

      {notes.status === "ready" && coverage !== null ? (
        <div className="readywrap">
          <div className="readyring">
            <svg viewBox="0 0 120 120" role="img" aria-label={readinessLabel(coverage)}>
              <title>{readinessLabel(coverage)}</title>
              <circle className="track" cx="60" cy="60" r={READY_RADIUS} />
              {measurable ? (
                <circle
                  className="fill"
                  cx="60"
                  cy="60"
                  r={READY_RADIUS}
                  data-testid="report-readiness-arc"
                  strokeDasharray={`${String(filled)} ${String(READY_CIRCUMFERENCE)}`}
                />
              ) : null}
            </svg>
            <span className="readyring__num" data-testid="report-readiness-value">
              {measurable ? `${String(percent)}%` : "—"}
            </span>
          </div>
          <div className="readymeta" data-testid="readiness-meta">
            {measurable ? (
              <>
                <b>
                  {coverage.covered} of {coverage.total} categories covered
                </b>{" "}
                — a category counts as covered once it holds at least one quick note about this
                official. {coverage.entries} note{coverage.entries === 1 ? "" : "s"} in total;
                notes filed under <b>General</b> are counted as entries but cover no grading
                category, because General is not one.
              </>
            ) : (
              <>
                <b>No grading categories apply to this role</b> — nothing in the system points a
                marking at this official (ruling R2&prime;), so there is no denominator to
                measure against. The ring is drawn empty rather than at 0%.
              </>
            )}
            {measurable && coverage.uncovered.length > 0 ? (
              <div className="readymiss" data-testid="readiness-uncovered">
                {coverage.uncovered.map((label) => (
                  <span key={label}>{label}</span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="panel__foot">
        <b>A real figure, including 0% —</b> readiness counts the grading categories that already
        hold one of your quick notes, over the categories this official is marked on. It is a
        measure of your evidence base and not of the official: <b>0%</b> means nothing has been
        recorded yet, which is a fact about the notebook. The chips name the categories still
        unobserved. Notes are written pitch-side and corrected in <b>Quick Notes</b>.
      </div>
    </section>
  );
}

/** The ring's screen-reader label — the same sentence for the tooltip and the `aria-label`. */
function readinessLabel(coverage: TdFeedbackCoverage): string {
  if (coverage.total === 0) {
    return "Report Readiness: this official is marked on no grading categories, so readiness has no denominator.";
  }
  return (
    `Report Readiness: ${String(coverage.covered)} of ${String(coverage.total)} grading ` +
    "categories hold at least one quick note. It measures the evidence base for the report, " +
    "not the official's performance."
  );
}

/* ────────────────────────────── §B11 · TD Feedback ──────────────────────── */

/**
 * §B11 `TD Feedback` — WIRED to Quick Notes, run 51.
 *
 * **Alfred's ruling (2026-09-03), verbatim in intent:** *"Keep the TD Feedback panel and show
 * Quick Notes data so TD can look at previous feedbacks on the same page where the TD will draft
 * his report."* This panel had said since the early build that *"this build keeps no such
 * store"*; the store arrived with `td_note` (migration 037) and the sentence was left behind. It
 * now reads that store.
 *
 * **Why it is not simply a second Quick Notes list.** Quick Notes is the notebook — newest
 * first, five to a page, the reading order of a diary. This is the notebook re-sorted into the
 * shape of the report the TD is about to write: every grading category in the order it is marked
 * in, the notes filed under each, and — the reason the panel earns its place — the categories
 * that hold NOTHING, named. `Which have I not observed yet` is a question the diary order cannot
 * answer at a glance and this one answers without being asked.
 *
 * **READ-ONLY, deliberately.** Corrections belong on one surface, and that surface is Quick
 * Notes (`quick-note-edit*`). Two edit paths to one note on one page is two chances to lose the
 * TD's words to a stale overlay, and the panel and the notebook already read the same state.
 */
export function TdFeedbackPanel({
  role,
  notes,
}: {
  /** Whose criteria set orders the groups, or null while the official is still loading. */
  role: OfficialRole | null;
  /** The page's one notes read — see {@link useTdNotes}. */
  notes: TdNotesState;
}): React.JSX.Element {
  const coverage = role === null ? null : tdFeedbackCoverage(notes.notes, role);

  return (
    <section className="panel panel--d2" aria-labelledby="fbTitle" data-howto="td-feedback">
      <div className="panel__head">
        <h3 className="panel__title" id="fbTitle">
          TD Feedback
        </h3>
        <span className="spacer" />
        <span className="panel__note" data-testid="td-feedback-note">
          {coverage === null || notes.status !== "ready"
            ? "Reading Your Quick Notes"
            : `${String(coverage.entries)} ${coverage.entries === 1 ? "Entry" : "Entries"} · ` +
              `${String(coverage.covered)} Of ${String(coverage.total)} Categories Covered`}
        </span>
      </div>

      {notes.status === "loading" || role === null ? <Skeleton rows={3} /> : null}
      {notes.error === null ? null : <FailBanner error={notes.error} />}

      {notes.status === "ready" && coverage !== null && coverage.entries === 0 ? (
        <EmptyState
          title="No Feedback Is Recorded Against This Official"
          detail="Feedback is your own quick notes — captured pitch-side, on this page or on your phone, against one grading category at a time. Nothing has been written about this official yet, so there is nothing to group here. The comments are the evidence and the mark is the conclusion, which is why Report Readiness reads 0% beside it: a fact about the notebook, not about the person."
        />
      ) : null}

      {coverage !== null && coverage.groups.length > 0 ? (
        <div className="fbgroups" data-testid="td-feedback-groups">
          {coverage.groups.map((group) => (
            <div className="fbgroup" data-testid="td-feedback-group" key={group.key ?? "general"}>
              <div className="fbgroup__head">
                <span className="kicker">{group.label}</span>
                <span className="spacer" />
                <span className="fbgroup__count num">
                  {group.notes.length} Note{group.notes.length === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="fblist">
                {group.notes.map((note) => (
                  <li key={note.id} data-testid="td-feedback-entry">
                    <span className="when">{quickNoteWhen(note.createdAt)}</span>
                    {note.matchId === null ? null : (
                      <span className="cat" title="The match this note was captured at">
                        Match {note.matchId}
                      </span>
                    )}
                    <span className="txt">{note.body}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}

      {coverage !== null && coverage.uncovered.length > 0 && notes.status === "ready" ? (
        <div className="fbmiss" data-testid="td-feedback-uncovered">
          <span className="fbmiss__lead">Nothing Recorded Yet</span>
          {coverage.uncovered.map((label) => (
            <span className="fbmiss__cat" key={label}>
              {label}
            </span>
          ))}
        </div>
      ) : null}

      <div className="panel__foot">
        <b>Your quick notes, grouped by the category you filed them under.</b> Feedback is written
        pitch-side — on this page&rsquo;s <b>Quick Notes</b> or from your phone — and this panel
        only reads it, so a typo is corrected there, on the note itself, and never twice on one
        screen. <b>General</b> notes count in the entry total but cover no grading category,
        because General is not one:{" "}
        <b>
          {coverage === null ? "—" : coverage.covered} of{" "}
          {coverage === null ? "—" : coverage.total} categories covered
        </b>{" "}
        counts only the categories you are marking on. The categories named above as{" "}
        <b>Nothing Recorded Yet</b> are the gaps in the evidence base, not gaps in the official.
      </div>
    </section>
  );
}

/* ────────────────────────────── §B5 helper, exported for reuse ──────────── */

/** A country cell as both the roster and this page draw it: flag then IOC code. */
export function CountryCell({ code }: { code: string }): React.JSX.Element {
  return <CountryFlag code={code} />;
}
